import SysRecord from 'app/class/record';
import { useFormContext } from 'react-hook-form';
import useRecordProducerActions from 'modules/record/record-producer/hooks/useRecordProducerActions';
import useRecordProducerActionCompiler from 'modules/record/record-producer/hooks/useRecordProducerActionCompiler';
import { RecordProducerRelatedActionsComponent } from './RecordProducerRelatedActions';
import { useSession } from 'app/hooks/useSession';
import { ReactNode, useContext, useEffect, useState } from 'react';
import FormActionConfirmationDialog, { requiresFormActionConfirmation } from 'modules/record/shared/components/FormActionConfirmationDialog';
import ResponsiveActionBar from 'modules/record/shared/components/ResponsiveActionBar';
import useActionHotkeys from 'modules/record/shared/hooks/useActionHotkeys';
import { allowsRecordAction, RecordPermissions } from 'modules/record/shared/services/actionPermissions';
import { RecordProducerContext } from './RecordProducerContext';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { translateMetadataLabel } from 'modules/record/shared/services/recordMetadataLabels';

const EMBEDDED_HIDDEN_ACTIONS = new Set(['Delete', 'package.action.add_current']);

interface RecordProducerActionsComponentProps {
    tableName: string;
    id?: string;
    leadingContent?: ReactNode;
    permissions?: RecordPermissions;
    onSaveReady?: (save: (() => Promise<boolean>) | null) => void;
}

export const RecordProducerActionsComponent = ({ tableName, id, leadingContent, permissions, onSaveReady }: RecordProducerActionsComponentProps) => {
    const { t } = useTranslation();
    const producer = useContext(RecordProducerContext);
    const embedded = producer?.embedded === true;
    const { getValues } = useFormContext();
    const { data: userSession } = useSession();
    const { actions } = useRecordProducerActions(tableName, {
        form: getValues(),
        currentTableName: tableName,
        isNewRecord: !id,
        user: { id: userSession?.id || "", roles: userSession?.roles || [] }
    });
    const allowedActions = actions.filter(action => allowsRecordAction(action, permissions) &&
        (!embedded || !EMBEDDED_HIDDEN_ACTIONS.has(action.label)));
    const preferredSave = allowedActions.find((action) => action.label === 'Save') ||
        allowedActions.find((action) => action.label === 'Update');
    const permittedActions = embedded
        ? allowedActions.filter((action) => !['Save', 'Update'].includes(action.label) || action === preferredSave)
        : allowedActions;
    const { runActionScript } = useRecordProducerActionCompiler();
    const saveAction = permittedActions.find((action) => ["Save", "Update"].includes(action.label));
    useEffect(() => {
        onSaveReady?.(saveAction ? () => runActionScript(saveAction, tableName, "form", true) : null);
        return () => onSaveReady?.(null);
    }, [onSaveReady, saveAction, runActionScript, tableName]);
    const [pendingAction, setPendingAction] = useState<SysRecord | null>(null);
    const [runningActionId, setRunningActionId] = useState<string | null>(null);

    const executeAction = async (action: SysRecord) => {
        setRunningActionId(action.id);

        try {
            await runActionScript(action, tableName);
        } finally {
            setRunningActionId(null);
        }
    };

    const handleActionClick = (action: SysRecord) => {
        if (runningActionId) return;

        if (requiresFormActionConfirmation(action)) {
            setPendingAction(action);
            return;
        }

        executeAction(action);
    };

    const handleConfirmAction = async () => {
        const action = pendingAction;
        setPendingAction(null);

        if (action) {
            await executeAction(action);
        }
    };

    useActionHotkeys({
        actions: permittedActions,
        runningActionId,
        onActionTrigger: handleActionClick,
    });

    if (!permittedActions.length) return null;

    return (
        <>
            <ResponsiveActionBar
                actions={permittedActions}
                runningActionId={runningActionId}
                mobileVisibleCount={1}
                variant="form"
                leadingContent={embedded ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                        {producer?.table ? translateMetadataLabel(t, producer.table) : tableName}
                    </Typography>
                    {leadingContent}
                </Box> : leadingContent}
                getActionLabel={embedded ? (action) => ['Save', 'Update'].includes(action.label)
                    ? String(t('fluss.task.save_record', { defaultValue: 'Save' })) : undefined : undefined}
                onActionClick={handleActionClick}
                trailingContent={<RecordProducerRelatedActionsComponent tableName={tableName} id={id} />}
            />
            <FormActionConfirmationDialog
                action={pendingAction}
                record={getValues()}
                open={Boolean(pendingAction)}
                onCancel={() => setPendingAction(null)}
                onConfirm={handleConfirmAction}
            />
        </>
    )
}
